import type { SerializerOptions } from '@ember-data/legacy-compat';
import type { Snapshot } from '@ember-data/legacy-compat/-private';
import type { ModelSchema } from '@ember-data/store/types';

import type { RecordAttributes, RecordData } from 'webapp-types';
import { pick } from 'webapp/utils/common';

import ApplicationSerializer from 'webapp/serializers/application';

import type TeamSectionItemRecord from 'webapp/models/team-section-item';

export default class TeamSectionItemSerializer extends ApplicationSerializer {
	// json is Raw when it comes from BE, Serialized when BufferedModel.toJSON() runs a record through normalize
	override normalize(
		typeClass: ModelSchema,
		json: RecordData.TeamSectionItem.Raw | RecordData.TeamSectionItem.Serialized,
		prop: string | undefined,
	) {
		const { objectData, ...data } = json;
		// objectData can be null before the object transform turns it into {}
		const attributes: Partial<Omit<RecordAttributes<TeamSectionItemRecord>, 'objectData'>> & {
			id?: string | number;
			objectData?: RecordData.TeamSectionItem.ObjectData | null;
		} = { ...data, objectData };

		if (objectData) {
			// the metric setup lives in the metricSetup* attributes
			const { metricSetup, ...otherObjectData } = objectData;
			attributes.objectData = otherObjectData;

			if (metricSetup) {
				const normalizedMetricSetup = this.normalizeMetricSetupData(metricSetup)!;
				attributes.metricSetupKey = normalizedMetricSetup.key;
				attributes.metricSetupSpaceAccessId = normalizedMetricSetup.spaceAccessId;
				attributes.metricSetupDimensions = normalizedMetricSetup.dimensions || [];
				attributes.metricSetupFilters = normalizedMetricSetup.filters || null;
			}

			if (objectData.dateRange) {
				attributes.dateRange = objectData.dateRange;
			}
		}

		return super.normalize(typeClass, attributes, prop);
	}

	override serialize(
		snapshot: Snapshot,
		options: SerializerOptions,
	): RecordData.TeamSectionItem.Serialized {
		const attributes = super.serialize(
			snapshot,
			options,
		) as RecordAttributes<TeamSectionItemRecord> & {
			id?: string;
		};

		return pick(attributes, 'id', 'objectType', 'objectData');
	}
}
